import {
  BUILDER_LINKS,
  BuilderLinkKey,
  LensId,
  ProtectionId,
  builderPrices,
  lensById,
  remainingLenses,
} from '../data/builder'
import { PRODUCT_LINKS, ProductLinkKey, variantIdOf } from '../data/productLinks'

export interface BuildState {
  lens: LensId
  fullLens: boolean
  hardCase: boolean
  careKit: boolean
  rx: number
  protection: ProtectionId
  complete: boolean
}

export interface BuildLine {
  label: string
  price: number
  quantity: number
  source: 'builder' | 'product'
  key: BuilderLinkKey | ProductLinkKey
}

export type CompleteHint =
  | { kind: 'none' }
  | { kind: 'near'; diff: number }
  | { kind: 'close'; diff: number }
  | { kind: 'better'; core: number; save: number }

export const SHOP_CART_BASE = 'https://shop.gritvuegear.com/cart'

const round = (value: number) => Math.round(value * 100) / 100

export const initialBuild: BuildState = {
  lens: 'clear',
  fullLens: false,
  hardCase: false,
  careKit: false,
  rx: 0,
  protection: 'basic',
  complete: false,
}

/** Every line the customer explicitly chose, in the order it should appear in the cart. */
export function buildLines(state: BuildState): BuildLine[] {
  const lens = lensById(state.lens)
  const lines: BuildLine[] = []

  if (state.complete) {
    const lifetime = state.protection === 'lifetime'
    lines.push({
      label: lifetime ? 'X1 Complete System + Lifetime Replacement Warranty' : 'X1 Complete System',
      price: lifetime ? builderPrices.complete + builderPrices.completeLifetime : builderPrices.complete,
      quantity: 1,
      source: 'product',
      key: lifetime ? 'singleX1LifetimeNormal6990' : 'singleX1',
    })
  } else {
    lines.push({
      label: `X1 Essential · ${lens.name} lens`,
      price: builderPrices.essential,
      quantity: 1,
      source: 'builder',
      key: lens.link,
    })
    if (state.fullLens) {
      lines.push({ label: 'Full Lens System', price: builderPrices.fullLensSystem, quantity: 1, source: 'builder', key: 'fullLensSystem' })
    }
    if (state.hardCase) {
      lines.push({ label: 'Hard Protective Case', price: builderPrices.hardCase, quantity: 1, source: 'builder', key: 'hardCase' })
    }
  }

  if (state.careKit) {
    lines.push({ label: 'Anti-Fog Care Kit', price: builderPrices.careKit, quantity: 1, source: 'builder', key: 'careKit' })
  }
  if (state.rx > 0) {
    lines.push({ label: 'RX Capability Insert', price: builderPrices.rxInsert, quantity: state.rx, source: 'builder', key: 'rxInsert' })
  }
  if (!state.complete && state.protection === 'oneYear') {
    lines.push({ label: '1-Year Breakage Protection', price: builderPrices.oneYearProtection, quantity: 1, source: 'builder', key: 'oneYearProtection' })
  }
  if (!state.complete && state.protection === 'lifetime') {
    lines.push({ label: 'Lifetime Breakage Protection', price: builderPrices.lifetimeProtection, quantity: 1, source: 'builder', key: 'lifetimeProtection' })
  }
  return lines
}

export function buildTotal(lines: BuildLine[]): number {
  return round(lines.reduce((sum, line) => sum + line.price * line.quantity, 0))
}

/** Only the components the Complete System replaces: Essential + lens system + case. */
export function coreSubtotal(state: BuildState): number {
  if (state.complete) return builderPrices.complete
  return round(
    builderPrices.essential +
      (state.fullLens ? builderPrices.fullLensSystem : 0) +
      (state.hardCase ? builderPrices.hardCase : 0),
  )
}

export function completeHint(state: BuildState): CompleteHint {
  if (state.complete) return { kind: 'none' }
  const core = coreSubtotal(state)
  const diff = round(builderPrices.complete - core)
  if (diff <= 0) return { kind: 'better', core, save: round(-diff) }
  if (state.hardCase) return { kind: 'close', diff }
  if (state.fullLens) return { kind: 'near', diff }
  return { kind: 'none' }
}

function linkFor(line: BuildLine): string {
  return line.source === 'product'
    ? PRODUCT_LINKS[line.key as ProductLinkKey]
    : BUILDER_LINKS[line.key as BuilderLinkKey]
}

export function missingLinks(lines: BuildLine[]): string[] {
  return lines.filter((line) => !variantIdOf(linkFor(line))).map((line) => line.label)
}

export function variantIds(lines: BuildLine[]): string[] {
  return lines.map((line) => variantIdOf(linkFor(line))).filter(Boolean)
}

/** Readable build summary for the fulfillment team, carried on the order as cart attributes. */
export function buildAttributes(state: BuildState, total: number): Record<string, string> {
  return {
    Configuration: state.complete ? 'X1 Complete System' : 'X1 Essential',
    'Primary Lens': state.complete ? 'Clear + Amber + Smoke (Complete)' : lensById(state.lens).name,
    'Full Lens System Upgrade': state.complete ? 'Included in Complete' : state.fullLens ? 'Yes' : 'No',
    'Additional Lenses':
      state.complete
        ? 'Included in Complete'
        : state.fullLens
          ? remainingLenses(state.lens).map((lens) => lens.name).join(' + ')
          : 'None',
    'Hard case': state.complete || state.hardCase ? 'Yes' : 'No',
    'Care kit': state.careKit ? 'Yes' : 'No',
    'RX inserts': String(state.rx),
    Protection: protectionLabel(state),
    'X1 systems': '1',
    'Build total': `$${total.toFixed(2)}`,
  }
}

export function protectionLabel(state: BuildState): string {
  if (state.complete) return state.protection === 'lifetime' ? 'Lifetime Replacement Warranty' : '1-Year Replacement Coverage (included)'
  if (state.protection === 'oneYear') return '1-Year Breakage Protection'
  if (state.protection === 'lifetime') return 'Lifetime Breakage Protection'
  return '30-Day Basic Coverage (included)'
}

/**
 * One Shopify cart permalink carrying every chosen line plus the build summary.
 * Returns '' when any chosen line has no connected Shopify product.
 */
export function buildCartUrl(lines: BuildLine[], attributes: Record<string, string>): string {
  if (missingLinks(lines).length > 0) return ''
  const items = lines.map((line) => `${variantIdOf(linkFor(line))}:${line.quantity}`).join(',')
  const params = new URLSearchParams()
  Object.entries(attributes).forEach(([key, value]) => params.append(`attributes[${key}]`, value))
  return `${SHOP_CART_BASE}/${items}?${params.toString()}`
}
